import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Carousel } from "../../home/slider";

import { fetchMatkaMarket, fetchMatkaMarkets } from "../../../api/services/matka";
import { MATKA_DATA, GAME_TYPES } from "./constants";
import { mapMarketBetTypes, toMarketCards } from "./mapMatkaMarket";
import { getSessionOptions } from "./MatkaSessionSelect";
import MatkaMarketsView from "./MatkaMarketsView";
import MatkaDailyResults from "./MatkaDailyResults";
import MatkaMonthlyResults from "./MatkaMonthlyResults";
import MatkaBetHistory from "./MatkaBetHistory";
import MatkaGameTypesView from "./MatkaGameTypesView";
import MatkaSingleDigitBetting from "./MatkaSingleDigitBetting";
import MatkaSingleDigitBulk from "./MatkaSingleDigitBulk";
import MatkaJodiDigit from "./MatkaJodiDigit";
import MatkaJodiDigitBulk from "./MatkaJodiDigitBulk";
import MatkaSinglePana from "./MatkaSinglePana";
import MatkaSinglePanaBulk from "./MatkaSinglePanaBulk";
import MatkaDoublePana from "./MatkaDoublePana";
import MatkaDoublePanaBulk from "./MatkaDoublePanaBulk";
import MatkaTriplePana from "./MatkaTriplePana";
import MatkaSpMotor from "./MatkaSpMotor";
import MatkaDpMotor from "./MatkaDpMotor";
import MatkaGroupJodi from "./MatkaGroupJodi";
import MatkaSpDpTp from "./MatkaSpDpTp";
import MatkaOddEven from "./MatkaOddEven";
import MatkaRedBracket from "./MatkaRedBracket";
import MatkaDigitBasedJodi from "./MatkaDigitBasedJodi";
import MatkaChoicePana from "./MatkaChoicePana";
import MatkaPanelGroup from "./MatkaPanelGroup";
import MatkaTwoDigitPanel from "./MatkaTwoDigitPanel";
import MatkaHalfSangamA from "./MatkaHalfSangamA";
import MatkaHalfSangamB from "./MatkaHalfSangamB";
import MatkaFullSangam from "./MatkaFullSangam";
import MatkaFooter from "./MatkaFooter";

export default function MatkaContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.user);

  const [activeTab, setActiveTab] = useState("Market");
  const [marketSection, setMarketSection] = useState("Markets");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedGameType, setSelectedGameType] = useState(null);
  // Betting Form State
  const [sessionStatus, setSessionStatus] = useState("OPEN");
  const [digitPoints, setDigitPoints] = useState({
    "1": "",
    "2": "",
    "3": "",
    "4": "",
    "5": "",
    "6": "",
    "7": "",
    "8": "",
    "9": "",
    "0": "",
  });
  const [betSuccessMessage, setBetSuccessMessage] = useState(null);
  const [marketCards, setMarketCards] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [marketsError, setMarketsError] = useState("");
  const [marketDetail, setMarketDetail] = useState(null);
  const [marketDetailLoading, setMarketDetailLoading] = useState(false);
  const [marketDetailError, setMarketDetailError] = useState("");

  const tabs = ["Starline", "Market", "Jackpot"];
  const isLoggedIn = Boolean(user?.userLoggedIn);
  const marketSubTabs = isLoggedIn
    ? ["Markets", "Daily Results", "Monthly Result", "Bet History"]
    : ["Markets", "Daily Results", "Monthly Result"];

  useEffect(() => {
    if (!isLoggedIn && marketSection === "Bet History") {
      setMarketSection("Markets");
    }
  }, [isLoggedIn, marketSection]);

  useEffect(() => {
    let active = true;
    setMarketsLoading(true);
    setMarketsError("");

    fetchMatkaMarkets()
      .then((markets) => {
        if (!active) return;
        setMarketCards(toMarketCards(markets));
      })
      .catch(() => {
        if (!active) return;
        setMarketCards([]);
        setMarketsError("Unable to load markets.");
      })
      .finally(() => {
        if (active) setMarketsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const allMarkets = useMemo(
    () => [...marketCards, ...MATKA_DATA.Starline, ...MATKA_DATA.Jackpot],
    [marketCards]
  );

  const apiGameTypes = useMemo(
    () => mapMarketBetTypes(marketDetail?.bet_types),
    [marketDetail]
  );

  // Sync with URL query parameters ?market=...&game=...
  useEffect(() => {
    const marketParam = searchParams.get("market");
    const gameParam = searchParams.get("game");

    if (marketParam) {
      const foundMarket = allMarkets.find((m) => m.id === marketParam);
      if (foundMarket) {
        setSelectedMarket(foundMarket);
      }
    } else {
      setSelectedMarket(null);
    }

    if (gameParam) {
      const foundGame =
        apiGameTypes.find((gameType) => gameType.id === gameParam) ||
        GAME_TYPES.find((gameType) => gameType.id === gameParam);
      if (foundGame) {
        setSelectedGameType(foundGame);
      }
    } else {
      setSelectedGameType(null);
    }
  }, [searchParams, allMarkets, apiGameTypes]);

  useEffect(() => {
    const marketId = selectedMarket?.marketId;
    if (!marketId) {
      setMarketDetail(null);
      setMarketDetailError("");
      setMarketDetailLoading(false);
      return undefined;
    }

    let active = true;
    setMarketDetail(null);
    setMarketDetailLoading(true);
    setMarketDetailError("");

    fetchMatkaMarket(marketId)
      .then((market) => {
        if (active) setMarketDetail(market);
      })
      .catch(() => {
        if (!active) return;
        setMarketDetail(null);
        setMarketDetailError("Unable to load market.");
      })
      .finally(() => {
        if (active) setMarketDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedMarket?.marketId]);

  const filteredMarkets = useMemo(() => {
    const list = activeTab === "Market" ? marketCards : MATKA_DATA[activeTab] || [];
    if (!searchTerm.trim()) return list;
    const query = searchTerm.toLowerCase().trim();
    return list.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.numbers.toLowerCase().includes(query)
    );
  }, [activeTab, searchTerm, marketCards]);

  // Points calculation
  const totalBids = useMemo(() => {
    return Object.values(digitPoints).filter(
      (val) => val && Number(val) > 0
    ).length;
  }, [digitPoints]);

  const totalPoints = useMemo(() => {
    return Object.values(digitPoints).reduce((sum, val) => {
      const num = Number(val);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  }, [digitPoints]);

  const handlePointChange = (digit, value) => {
    if (value === "" || (/^\d+$/.test(value) && Number(value) >= 0)) {
      setDigitPoints((prev) => ({
        ...prev,
        [digit]: value,
      }));
    }
  };

  const clearDigitPoints = () => {
    setDigitPoints({
      1: "",
      2: "",
      3: "",
      4: "",
      5: "",
      6: "",
      7: "",
      8: "",
      9: "",
      0: "",
    });
  };

  const handleSelectMarket = (item) => {
    setSelectedMarket(item);
    setSelectedGameType(null);
    setSearchParams({ market: item.id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToMarkets = () => {
    setSelectedMarket(null);
    setSelectedGameType(null);
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectGameType = (gameType) => {
    setSelectedGameType(gameType);
    setSessionStatus(getSessionOptions(gameType.betDirection)[0]);
    setSearchParams({
      market: selectedMarket?.id || "mahadevi",
      game: gameType.id,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToGameTypes = () => {
    setSelectedGameType(null);
    setSearchParams({
      market: selectedMarket?.id || "mahadevi",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleQuickMarketSelect = (marketId) => {
    const found = allMarkets.find((m) => m.id === marketId);
    if (found) {
      setSelectedMarket(found);
      setSearchParams({
        market: found.id,
        game: selectedGameType?.id || "single-digit",
      });
    }
  };

  return (
    <div className="matka-page-wrapper">
      {/* Toast Notification */}
      {betSuccessMessage && (
        <div
          className="toast align-items-center text-bg-warning fade show position-fixed top-0 end-0 m-4 p-3 shadow-lg"
          style={{ zIndex: 9999 }}
          role="alert"
        >
          <div className="d-flex align-items-center">
            <div className="toast-body fw-bold text-dark">{betSuccessMessage}</div>
            <button
              type="button"
              className="btn-close me-2 m-auto"
              onClick={() => setBetSuccessMessage(null)}
            ></button>
          </div>
        </div>
      )}

      {/* Conditional Screen Rendering */}
      {selectedMarket && selectedGameType ? (
        selectedGameType.id === "single-digit-bulk" ? (
          /* Single Digit Bulk View */
          <MatkaSingleDigitBulk
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            sessionStatus={sessionStatus}
            onSessionStatusChange={setSessionStatus}
            digitPoints={digitPoints}
            onPointChange={handlePointChange}
            onClearPoints={clearDigitPoints}
            totalBids={totalBids}
            totalPoints={totalPoints}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : selectedGameType.id === "jodi-digit" ? (
          /* Jodi Digit View */
          <MatkaJodiDigit
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : selectedGameType.id === "jodi-digit-bulk" ? (
          /* Jodi Digit Bulk View */
          <MatkaJodiDigitBulk
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : selectedGameType.id === "single-pana" ? (
          /* Single Pana View */
          <MatkaSinglePana
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "single-pana-bulk" || selectedGameType.id === "single-panna-bulk") ? (
          /* Single Pana Bulk View */
          <MatkaSinglePanaBulk
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "double-pana" || selectedGameType.id === "double-panna") ? (
          /* Double Pana View */
          <MatkaDoublePana
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "double-pana-bulk" || selectedGameType.id === "double-panna-bulk") ? (
          /* Double Panna Bulk View */
          <MatkaDoublePanaBulk
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "triple-pana" || selectedGameType.id === "triple-panna" || selectedGameType.id === "tripple-pana" || selectedGameType.id === "tripple-panna") ? (
          /* Triple Pana View */
          <MatkaTriplePana
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "sp-motor") ? (
          /* SP Motor View */
          <MatkaSpMotor
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "dp-motor") ? (
          /* DP Motor View */
          <MatkaDpMotor
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "group-jodi") ? (
          /* Group Jodi View */
          <MatkaGroupJodi
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "sp-dp-tp") ? (
          /* SP DP TP View */
          <MatkaSpDpTp
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "odd-even") ? (
          /* Odd Even View */
          <MatkaOddEven
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "red-bracket") ? (
          /* Red Bracket View */
          <MatkaRedBracket
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "digit-based-jodi") ? (
          /* Digit Based Jodi View */
          <MatkaDigitBasedJodi
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "choice-pana") ? (
          /* Choice Panna View */
          <MatkaChoicePana
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "panel-group") ? (
          /* Panel Group View */
          <MatkaPanelGroup
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "two-digit-panel") ? (
          /* Two Digit Panel View */
          <MatkaTwoDigitPanel
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "half-sangam-a") ? (
          /* Half Sangam A View */
          <MatkaHalfSangamA
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "half-sangam-b") ? (
          /* Half Sangam B View */
          <MatkaHalfSangamB
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (selectedGameType.id === "full-sangam") ? (
          /* Full Sangam View */
          <MatkaFullSangam
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
            onSelectTab={setActiveTab}
          />
        ) : (
          /* Single Digit Betting View */
          <MatkaSingleDigitBetting
            selectedMarket={selectedMarket}
            selectedGameType={selectedGameType}
            sessionStatus={sessionStatus}
            onSessionStatusChange={setSessionStatus}
            digitPoints={digitPoints}
            onPointChange={handlePointChange}
            onClearPoints={clearDigitPoints}
            totalBids={totalBids}
            totalPoints={totalPoints}
            onBackToGameTypes={handleBackToGameTypes}
            onBackToMarkets={handleBackToMarkets}
            onQuickMarketSelect={handleQuickMarketSelect}
          />
        )
      ) : (
        /* Screen 1 & Screen 2: Markets Grid or Game Types Grid with Top Carousel */
        <>
          <Carousel />

          {!selectedMarket ? (
            /* Screen 1: Main Markets View */
            <MatkaMarketsView
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              subTabs={activeTab === "Market" ? marketSubTabs : []}
              activeSubTab={marketSection}
              onSubTabChange={setMarketSection}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              markets={filteredMarkets}
              isLoading={activeTab === "Market" && marketSection === "Markets" && marketsLoading}
              errorMessage={activeTab === "Market" && marketSection === "Markets" ? marketsError : ""}
              onSelectMarket={handleSelectMarket}
              hideSearch={activeTab === "Market" && marketSection === "Monthly Result"}
              content={
                activeTab === "Market" && marketSection === "Daily Results" ? (
                  <MatkaDailyResults searchTerm={searchTerm} />
                ) : activeTab === "Market" && marketSection === "Monthly Result" ? (
                  <MatkaMonthlyResults markets={marketCards} marketsLoading={marketsLoading} />
                ) : activeTab === "Market" && marketSection === "Bet History" && isLoggedIn ? (
                  <MatkaBetHistory searchTerm={searchTerm} />
                ) : null
              }
            />
          ) : (
            /* Screen 2: Game Types View */
            <MatkaGameTypesView
              selectedMarket={selectedMarket}
              gameTypes={
                selectedMarket?.marketId
                  ? mapMarketBetTypes(marketDetail?.bet_types)
                  : GAME_TYPES
              }
              isLoading={Boolean(selectedMarket?.marketId) && marketDetailLoading}
              errorMessage={selectedMarket?.marketId ? marketDetailError : ""}
              onBack={handleBackToMarkets}
              onSelectGameType={handleSelectGameType}
            />
          )}
          {/* Matka Footer (Only on Markets and Game Types views) */}
          <MatkaFooter
            onSelectTab={setActiveTab}
            onBackToMarkets={handleBackToMarkets}
          />
        </>
      )}
    </div>
  );
}
